import { BdAddress } from './address.model';
import type { AysClinicNotfallversorgung } from './clinic-notfallversorgung';

/**
 * Raw clinic address as returned by clinics-api. Field names are German
 * (`plz`/`ort`); `postalCode`/`city` are accepted as a fallback in case the
 * contract is aligned to its OpenAPI schema later. Values may be null.
 */
interface AysClinicAddressResponse {
  street?: string | null;
  plz?: string | null;
  ort?: string | null;
  postalCode?: string | null;
  city?: string | null;
  location?: number[];
  administrativeDistrictId?: number;
}

/** Map the clinics-api address shape (`street`/`plz`/`ort`) to a BdAddress. */
function toClinicAddress(raw: unknown): BdAddress {
  const address = (raw ?? {}) as AysClinicAddressResponse;

  return BdAddress.fromObject({
    street: address.street ?? undefined,
    postalCode: address.plz ?? address.postalCode ?? undefined,
    city: address.ort ?? address.city ?? undefined,
    location: address.location,
    administrativeDistrictId: address.administrativeDistrictId,
  });
}

/** A coded value with an optional human label / short form. */
export interface AysCodeLabel {
  code: string;
  label?: string;
  kuerzel?: string;
}

export interface AysGeoLocation {
  lat: number;
  lng: number;
}

export interface AysClinicTraeger {
  traeger?: string;
  traegerart?: AysCodeLabel;
  rechtsform?: string;
  sitzGesellschaft?: string;
  registriernummer?: string;
  registriergericht?: string;
}

/** Raw response shape of `GET /clinics/{clinicSn}/metadata`. */
export interface AysClinicMetadataResponse {
  clinicSn: string;
  latestYear: string | number;
  clinicIk?: string;
  name?: string;
  address?: unknown;
  location?: AysGeoLocation;
  bundesland?: AysCodeLabel;
  kvBezirk?: AysCodeLabel;
  einrichtungstyp?: AysCodeLabel;
  isLiveNow: boolean;
  hasQbPublished: boolean;
  traeger?: AysClinicTraeger;
  size?: string;
  versorgungslevel?: string;
  notfallversorgung?: AysClinicNotfallversorgung;
}

/**
 * Registry metadata for a clinic (latest-snapshot; `size`/`versorgungslevel`
 * reflect the requested `?year=`, defaulting to the latest QB year).
 */
export class AysClinicMetadata {
  public static fromObj(obj: AysClinicMetadataResponse): AysClinicMetadata {
    return new AysClinicMetadata(
      obj.clinicSn,
      parseInt(obj.latestYear?.toString(), 10),
      toClinicAddress(obj.address),
      obj.isLiveNow,
      obj.hasQbPublished,
      obj.clinicIk,
      obj.name,
      obj.traeger,
      obj.location,
      obj.bundesland,
      obj.kvBezirk,
      obj.einrichtungstyp,
      obj.size,
      obj.versorgungslevel,
      obj.notfallversorgung,
    );
  }

  constructor(
    public clinicSn: string,
    public latestYear: number,
    public address: BdAddress,
    public isLiveNow: boolean,
    public hasQbPublished: boolean,
    public clinicIk?: string,
    public name?: string,
    public traeger?: AysClinicTraeger,
    public location?: AysGeoLocation,
    public bundesland?: AysCodeLabel,
    public kvBezirk?: AysCodeLabel,
    public einrichtungstyp?: AysCodeLabel,
    public size?: string,
    public versorgungslevel?: string,
    public notfallversorgung?: AysClinicNotfallversorgung,
  ) {}
}

/** Raw response item of `GET /clinics/search`. */
export interface AysClinicSearchResultResponse {
  clinicSn: string;
  latestYear: string | number;
  clinicIk?: string;
  name?: string;
  address?: unknown;
  location?: AysGeoLocation;
  bundesland?: AysCodeLabel;
  einrichtungstyp?: AysCodeLabel;
  isLiveNow: boolean;
  hasQbPublished: boolean;
}

export class AysClinicSearchResult {
  public static fromObj(
    obj: AysClinicSearchResultResponse,
  ): AysClinicSearchResult {
    return new AysClinicSearchResult(
      obj.clinicSn,
      parseInt(obj.latestYear?.toString(), 10),
      toClinicAddress(obj.address),
      obj.isLiveNow,
      obj.hasQbPublished,
      obj.clinicIk,
      obj.name,
      obj.location,
      obj.bundesland,
      obj.einrichtungstyp,
    );
  }

  constructor(
    public clinicSn: string,
    public latestYear: number,
    public address: BdAddress,
    public isLiveNow: boolean,
    public hasQbPublished: boolean,
    public clinicIk?: string,
    public name?: string,
    public location?: AysGeoLocation,
    public bundesland?: AysCodeLabel,
    public einrichtungstyp?: AysCodeLabel,
  ) {}
}

/**
 * Raw response item of `GET /clinics/by-distance/`. Unlike the SN-native search,
 * this endpoint (Clinic-Filter tag) is legacy-shaped: it nests the clinic under
 * a composite `id` ({ ik, sn }) and carries the geographic `distance` in a
 * `geometry` block.
 */
export interface AysClinicByDistanceResponse {
  clinic: {
    name?: string;
    /** Opaque Standortnummer — 9-digit iNEK or synthetic `L-{ik}-{sn}`. */
    clinicSn: string;
    clinicIk?: number | string;
    /** The internal ordinal (the pre-flatten `id.sn`); display/identity only. */
    internalSn?: number | string;
    year?: string;
    address?: unknown;
  };
  geometry?: { distance?: number | null } | null;
}

/**
 * A clinic returned by `GET /clinics/by-distance/`. The endpoint exposes clinic
 * identity as the flat `clinicSn`/`clinicIk`/`internalSn`/`year` quartet, so the
 * opaque `clinicSn` is taken straight off the wire and results link directly to
 * the SN-only detail route. `distanceMeters` is the geographic distance from the
 * search centre (null when the backend omits it).
 */
export class AysClinicByDistanceResult {
  public static fromObj(
    obj: AysClinicByDistanceResponse,
  ): AysClinicByDistanceResult {
    const clinic = obj.clinic;

    return new AysClinicByDistanceResult(
      clinic.clinicSn,
      toClinicAddress(clinic.address),
      obj.geometry?.distance ?? null,
      clinic.name,
      clinic.clinicIk?.toString(),
    );
  }

  constructor(
    public clinicSn: string,
    public address: BdAddress,
    public distanceMeters: number | null,
    public name?: string,
    public clinicIk?: string,
  ) {}
}

/**
 * Raw item of `POST /clinics/by-condition` (one matched clinic-year). Used to
 * batch-fetch specific clinics by clinicSn: the `clinicSn` condition accepts
 * opaque strings — iNEK Standortnummern and synthetic `L-{ik}-{sn}` ids alike.
 * The endpoint returns one row per (clinic × QB year); callers dedupe by
 * `clinicSn` (e.g. latest `year`).
 */
export interface AysClinicByConditionResponse {
  clinicSn: string;
  clinicIk?: number | string;
  year?: number;
  name?: string;
  address?: unknown;
  /** Number of beds — drives map label placement priority (BDA-5387). */
  beds?: number;
  /** Owner ("Träger"). */
  nameOfOwner?: string;
  /** Ownership category ("Trägerart"): `public` / `private` / `nonprofit`. */
  type?: string;
  /** Versorgungslevel (G-BA level). */
  level?: { id: string; displayName: { [locale: string]: string } };
}

/**
 * The classification and owner fields (`nameOfOwner`, `type`, `level`) are
 * QB-derived: they are always null for a registry-only clinic-year
 * (`hasQbData = false`), so consumers must treat them as optional.
 */
export class AysClinicByConditionResult {
  public static fromObj(
    obj: AysClinicByConditionResponse,
  ): AysClinicByConditionResult {
    return new AysClinicByConditionResult(
      obj.clinicSn,
      toClinicAddress(obj.address),
      obj.year,
      obj.name,
      obj.clinicIk?.toString(),
      obj.beds,
      obj.nameOfOwner,
      obj.type,
      obj.level,
    );
  }

  constructor(
    public clinicSn: string,
    public address: BdAddress,
    public year?: number,
    public name?: string,
    public clinicIk?: string,
    public beds?: number,
    public nameOfOwner?: string,
    public type?: string,
    public level?: { id: string; displayName: { [locale: string]: string } },
  ) {}
}
